import type { AdminFeedbackRow, AdminFeedbackSortBy } from '@ai-meeting/shared'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchAdminFeedback } from '@/services/feedback'
import AdminDataTable, { type AdminColumn } from './AdminDataTable'
import AdminSearchInput from './AdminSearchInput'
import { formatDateTime } from './adminFormat'
import { useAdminTable } from './useAdminTable'

interface FeedbackTabProps {
  enabled: boolean
}

/**
 * App-wide feedback tab (BRAI-185): paginated feedback comments with the
 * submitting user's name and feedback date, searchable across both.
 */
export default function FeedbackTab({ enabled }: FeedbackTabProps) {
  const { t, locale } = useUiStrings()

  const table = useAdminTable<AdminFeedbackRow, AdminFeedbackSortBy>({
    fetcher: fetchAdminFeedback,
    defaultSortBy: 'createdAt',
    enabled,
  })

  const columns: AdminColumn<AdminFeedbackRow, AdminFeedbackSortBy>[] = [
    { key: 'name', label: t.adminColName, sortBy: 'name', render: (r) => r.name },
    {
      key: 'comment',
      label: t.adminColComment,
      render: (r) => (
        <span className="block max-w-md whitespace-pre-wrap" title={r.comment}>
          {r.comment}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: t.adminColFeedbackAt,
      sortBy: 'createdAt',
      render: (r) => formatDateTime(r.createdAt, locale),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <AdminSearchInput
          value={table.search}
          placeholder={t.adminSearchFeedback}
          onChange={table.setSearch}
        />
      </div>
      <AdminDataTable
        columns={columns}
        rows={table.rows}
        rowKey={(r) => r.id}
        loading={table.loading}
        error={table.error}
        sortBy={table.sortBy}
        sortDir={table.sortDir}
        onToggleSort={table.toggleSort}
        page={table.page}
        totalPages={table.totalPages}
        total={table.total}
        onPageChange={table.setPage}
      />
    </div>
  )
}
